import ContentHeader from '../../components/UI/ContentHeader/ContentHeader';
import { MASTER_LINKS } from '../../constants/master-list-tabs';
import CareAreaList from './CareAreaList/CareAreaList';
import CareAreaService from '../../services/CareAreaService';
import { ReactQueryKeys } from '../../constants/react-query-keys';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { SweetAlertResult } from 'sweetalert2';
import { CareArea } from '../../interfaces/care-area';
import SwalAlert, {
  firePreConfirmAlert,
} from '../../components/UI/SwalAlert/SwalAlert';
import { toast } from 'react-hot-toast';
import { ADMINISTRATION, AUTHOR } from '../../constants/roles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Spinner from 'components/UI/Spinner/Spinner';
import HospitalTotalLabels from 'components/UI/HospitalTotalLabels/HospitalTotalLabels';
import { selectUser } from 'store/slices/auth';

/**
 * @description CareAreas component that displays a list of care areas with management functionality.
 * The component includes a sidebar with the list of care areas and a main content area
 * that renders child routes via the Outlet component. Features include pagination, filtering,
 * role-based access control, and delete functionality.
 *
 * @SADtrace SAD-003-3.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} The rendered CareAreas component
 */
const CareAreas = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const user = useSelector(selectUser);
  // v8 ignore start -- WAIVER(MICRO): user.role is always present on an authenticated session; the '' fallback exists only to satisfy the optional user type
  const canManage = [ADMINISTRATION, AUTHOR].includes(user?.role || '');
  // v8 ignore stop -- WAIVER(MICRO)

  /**
   * @description Fetches care areas data with pagination and filtering
   * Uses react-query to manage data fetching, caching, and state
   *
   * @SADtrace SAD-003-3.2
   */
  const {
    data: careAreas,
    isLoading: isLoadingCareAreas,
    isFetching,
  } = useQuery({
    queryKey: [
      ReactQueryKeys.CARE_AREAS,
      {
        columnFilters,
        pagination,
      },
    ],

    queryFn: () =>
      CareAreaService.get({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
        columnFilters: columnFilters,
      }),

    placeholderData: keepPreviousData,
  });

  /**
   * @description Updates search filters for the API call to filter care areas by name
   *
   * @SADtrace SAD-003-3.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string} value - The search value to filter care areas by name
   *
   * @returns {void}
   */
  const handleUpdateFilter = (value: string) => {
    setColumnFilters([
      {
        id: 'name',
        value,
      },
    ]);
  };

  /**
   * @description Shows confirmation alert for deleting a care area.
   * If confirmed, deletes the care area and updates the UI
   *
   * @SADtrace SAD-003-3.2, SAD-003-3.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {CareArea} careArea - The care area to be deleted
   *
   * @returns {void}
   */
  const handleDeleteShow = (careArea: CareArea) => {
    firePreConfirmAlert({
      title: t('care_areas.messages.modal__delete__title'),
      html: t('care_areas.messages.modal__delete__content', {
        name: careArea.name,
      }),
      preConfirm: () => {
        return CareAreaService.delete(String(careArea.id))
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
            //return false to prevent pop up from closing when running tests, check preconfirm fn
            return false;
          });
      },
    }).then((data: SweetAlertResult) => {
      // v8 ignore start -- WAIVER(THIRDPARTY): SweetAlert2 continuation — the dismissed arm resolves inside swal's promise contract with no application behavior to assert; the confirmed path is asserted
      if (data.isConfirmed) {
        // v8 ignore stop -- WAIVER(THIRDPARTY)
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.CARE_AREAS],
        });
        navigate('/care-areas');
        toast.success(t('care_areas.messages.delete__success'));
      }
    });
  };

  return (
    <>
      <ContentHeader links={MASTER_LINKS} />
      <div className="main-container with-tabs">
        <div className="row">
          <div className="col-3">
            {isLoadingCareAreas ? (
              <Spinner isAbsolute />
            ) : (
              <>
                <HospitalTotalLabels
                  translationKey="care_areas"
                  total={careAreas?.data.total}
                />

                <CareAreaList
                  refetch={isLoadingCareAreas || isFetching}
                  canManage={canManage}
                  onDelete={handleDeleteShow}
                  updateFilter={handleUpdateFilter}
                  careAreasResponse={careAreas?.data}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </>
            )}
          </div>
          <div className="col-9 ps-4">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
};

export default CareAreas;
